// src\app\page\customer-page\customer-page.component.ts
import { Component, inject, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { TableComponent } from 'src/app/components/table/table.component';
import { AppConstants } from 'src/app/constants/app-constants';
import { Customer } from 'src/app/model/Customer';
import {
  ColumnDefinition,
  ServiceInterface,
  TableConstruct,
} from 'src/app/model/Table';
import { CustomerService } from 'src/app/services/customer/customer.service';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { UserService } from 'src/app/services/user/user.service';

@Component({
  selector: 'app-customer-page',
  templateUrl: './customer-page.component.html',
  styleUrl: './customer-page.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CustomerPageComponent {
  /** Dependency injections */
  private _dialogService = inject(DialogService);
  private _customerService: CustomerService = inject(CustomerService);
  private _router: Router = inject(Router);
  private _userService: UserService = inject(UserService);

  /** Column definitions for the table */
  public columnDefinitions: ColumnDefinition<Customer>[] = [
    { field: 'accountId', header: 'ID' },
    { field: 'borrowerName', header: 'Business Name' },
    { field: 'accountLimit', header: 'Credit Limit', format: 'currency' },
    {
      field: 'availableBalance',
      header: 'Available Credit',
      format: 'currency',
    },
    { field: 'status', header: 'Status', format: 'status' },
    {
      field: 'actions',
      header: 'Actions',
      actions: [
        {
          display: 'Submit Request',
          click: this._submitRequestOnClick(),
          disable: (_) => {
            const companyStatus = this._userService.company?.companyStatus;
            if (!companyStatus) return false;

            // If the supplier company is suspended disable the button
            return companyStatus === 'suspended';
          },
          constraint: (customer: Customer) => {
            // Dont render the button if the customer has the closed or blocked status or has no available balance
            const disabledIf = new Set(['closed', 'blocked']);
            return (
              !disabledIf.has(customer.status.toLowerCase()) &&
              customer.availableBalance > 0
            );
          },
        },
      ],
    },
  ];

  /** Columns to display in the table */
  public displayedColumns: string[] = this.columnDefinitions.map(
    (x) => x.field,
  );

  /** Reference to the application table element */
  @ViewChild('customersTable') table!: TableComponent<Customer>;

  /** Construct table reference */
  public construct: TableConstruct<Customer>;

  /**
   * Constructor for the customers page to initialize services and set a base snapshot
   */
  constructor() {
    // Create a mock application service to provide to the table
    const mockService: ServiceInterface = {
      timeSinceLastRefresh$: this._customerService.timeSinceLastRefresh$,
      changes$: this._customerService.customers$,
      getEntities: () => this._customerService.get(),
    };

    // Create the table component
    this.construct = {
      source: new MatTableDataSource<Customer>(),
      columns: this.columnDefinitions,
      displayedColumns: this.displayedColumns,
      service: mockService,
      paginationRange: [5, 10, 25, 100],
      fetchConstraints: this._createFetchConstraints(),
      settings: { sort: { enabled: true } },
    };
  }

  /**
   * Constraints on when to fetch customer data. A return of true
   * blocks the source update
   */
  private _createFetchConstraints(): () => boolean {
    const self = this;

    return (): boolean => {
      return !self._router.url.endsWith(AppConstants.MENU_CUSTOMERS);
    };
  }

  /**
   * Return a function that handles the submit request button click
   */
  private _submitRequestOnClick(): (event: any, customer: Customer) => void {
    const self = this;

    return (event: any, customer: Customer): void => {
      // TODO: request/offer flow
      self._dialogService.openBuildOfferDialog(customer);
      //self._dialogService.openSubmitRequestDialog(customer);
    };
  }

  /**
   * Apply a filter to the table
   */
  public applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.table.construct.source.filter = filterValue.trim().toLowerCase();
  }

  /**
   * Export the table data to an xlsx file
   */
  public export(): void {
    this.table.exportCustomersToXlsx();
  }
}
